import { useCallback, useState, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb } from '../../../adapters/powersync';
import {
  acceptDiagnosisSuggestionStmt,
  dismissDiagnosisSuggestionStmt,
  type PatientDiagnosisSuggestionRow,
} from '@helios/data-access';

/* ------------------------------------------------------------------ */
/*  AI Suggested Diagnosis Card (PRD.217 §7.4)                        */
/*                                                                    */
/*  One row per pending `patient_diagnosis_suggestions` entry. Wraps  */
/*  Accept / Edit / Dismiss / View Details.                           */
/*                                                                    */
/*  Accept inserts a confirmed `patient_diagnosis_entries` row using  */
/*  the suggested label, then UPDATEs the suggestion via              */
/*  acceptDiagnosisSuggestionStmt — both writes flow through          */
/*  PowerSync → SyncWrite.                                            */
/*                                                                    */
/*  Edit is the same as Accept but with a clinician-edited label      */
/*  captured before the patient_diagnosis_entries row is inserted.    */
/* ------------------------------------------------------------------ */

interface Props {
  suggestion: PatientDiagnosisSuggestionRow;
  /** User id for accept/dismiss audit columns. Falls back to '' if missing. */
  userId: string;
  /** Org id stamped on the inserted patient_diagnosis_entries row. */
  orgId: string;
  /** Patient id stamped on the inserted patient_diagnosis_entries row. */
  patientId: string;
  /** Encounter id stamped on the inserted patient_diagnosis_entries row. */
  encounterId: string;
  /** Called when the user clicks "View Details". */
  onViewDetails: (analysisRunId: string) => void;
}

export function AiSuggestedDiagnosisCard({
  suggestion,
  userId,
  orgId,
  patientId,
  encounterId,
  onViewDetails,
}: Props) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(suggestion.diagnosis_text ?? '');
  const [busy, setBusy] = useState(false);

  const acceptWithLabel = useCallback(
    async (label: string) => {
      if (!db) return;
      const trimmed = label.trim();
      if (!trimmed) return;
      setBusy(true);
      try {
        const now = new Date().toISOString();
        const newEntryId = crypto.randomUUID();
        // 1) Insert the confirmed diagnosis. This is the same INSERT shape as
        //    the clinician-typed flow in EncounterDiagnosesTab.handleAdd.
        await db.execute(
          `INSERT INTO patient_diagnosis_entries
            (id, org_id, clinic_id, patient_id, encounter_id, label, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
          [newEntryId, orgId, null, patientId, encounterId, trimmed, now, now],
        );
        // 2) Flip the suggestion to accepted and link the new entry id.
        const stmt = acceptDiagnosisSuggestionStmt({
          suggestionId: suggestion.id,
          acceptedLabel: trimmed,
          acceptedDiagnosisEntryId: newEntryId,
          userId,
          now,
        });
        await db.execute(stmt.sql, [...stmt.params]);
      } finally {
        setBusy(false);
      }
    },
    [db, encounterId, orgId, patientId, suggestion.id, userId],
  );

  const handleAccept = useCallback(() => {
    void acceptWithLabel(suggestion.diagnosis_text ?? '');
  }, [acceptWithLabel, suggestion.diagnosis_text]);

  const handleConfirmEdit = useCallback(() => {
    void acceptWithLabel(draft).then(() => setEditing(false));
  }, [acceptWithLabel, draft]);

  const handleDismiss = useCallback(async () => {
    if (!db) return;
    setBusy(true);
    try {
      const now = new Date().toISOString();
      const stmt = dismissDiagnosisSuggestionStmt({
        suggestionId: suggestion.id,
        userId,
        now,
      });
      await db.execute(stmt.sql, [...stmt.params]);
    } finally {
      setBusy(false);
    }
  }, [db, suggestion.id, userId]);

  const handleViewDetails = useCallback(() => {
    if (suggestion.ai_analysis_run_id) onViewDetails(suggestion.ai_analysis_run_id);
  }, [onViewDetails, suggestion.ai_analysis_run_id]);

  return (
    <div
      style={{
        ...styles.card,
        borderColor: `${primary}55`,
        background: `${primary}07`,
      }}
    >
      {/* Top row: label + AI Suggested badge */}
      <div style={styles.headRow}>
        <div style={styles.labelCol}>
          {!editing ? (
            <div style={styles.diagnosis}>{suggestion.diagnosis_text || '—'}</div>
          ) : (
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
              style={styles.editInput}
              placeholder="Edit AI diagnosis…"
            />
          )}
          <div style={styles.meta}>
            {suggestion.confidence != null && (
              <span>Confidence {formatPct(suggestion.confidence)}</span>
            )}
            {suggestion.severity && <span>Severity {suggestion.severity}</span>}
          </div>
        </div>
        <span style={{ ...styles.badge, background: primary }}>
          <SparkleIcon />
          AI Suggested
        </span>
      </div>

      {/* Action row */}
      <div style={styles.actionRow}>
        {!editing ? (
          <>
            <Button variant="gradient" size="sm" onClick={handleAccept} disabled={busy}>
              Accept
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)} disabled={busy}>
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={handleDismiss} disabled={busy}>
              Dismiss
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewDetails}
              disabled={!suggestion.ai_analysis_run_id}
            >
              View Details
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="gradient"
              size="sm"
              onClick={handleConfirmEdit}
              disabled={busy || !draft.trim()}
            >
              Confirm
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDraft(suggestion.diagnosis_text ?? '');
                setEditing(false);
              }}
              disabled={busy}
            >
              Cancel
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Icons ──────────────────────────────────────────────────────────────────

function SparkleIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z" />
    </svg>
  );
}

// ─── Formatting ─────────────────────────────────────────────────────────────

function formatPct(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const pct = n > 1 ? n : n * 100;
  return `${Math.round(pct * 10) / 10}%`;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  card: {
    border: '1px solid',
    borderRadius: 'var(--radius-lg, 14px)',
    padding: 'var(--sp-3, 12px) var(--sp-4, 16px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--sp-2, 8px)',
  },
  headRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 'var(--sp-3, 12px)',
  },
  labelCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    flex: 1,
  },
  diagnosis: {
    fontSize: 'var(--font-size-md, 14px)',
    fontWeight: 600,
    color: 'var(--text, #111827)',
  },
  meta: {
    display: 'flex',
    gap: 'var(--sp-3, 12px)',
    fontSize: 'var(--font-size-xs, 11px)',
    color: 'var(--text-muted, #6b7280)',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    height: 22,
    padding: '0 8px',
    borderRadius: 'var(--radius-full, 9999px)',
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    flexShrink: 0,
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--sp-2, 8px)',
  },
  editInput: {
    width: '100%',
    padding: 'var(--sp-2, 8px) var(--sp-3, 12px)',
    borderRadius: 'var(--radius-md, 10px)',
    border: '1px solid var(--border-input, #d1d5db)',
    background: 'var(--input-bg, #fff)',
    fontSize: 'var(--font-size-sm, 12px)',
    color: 'var(--text, #111827)',
    outline: 'none',
  },
};
